import { Button, Group } from '@mantine/core';
import { DsaiIngestionProgressStatus } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import type { ProcessingCardActions, ProcessingCardContent } from './ProcessingCard.types';
import { DeleteBatchButton } from '../../BatchDeletion/DeleteBatchButton';

interface Props {
	phase: ProcessingCardContent['phase'];
	actions: ProcessingCardActions;
}

/**
 * Once processing has ended: "Open analysis" while the results open, else "Delete now" and Close; a failed batch also
 * offers "Delete batch" at the left.
 */
export const ProcessingSettledFooter = ({ phase, actions }: Props) => {
	const tCapManage = useTranslationCapManage();
	const isComplete = phase === DsaiIngestionProgressStatus.COMPLETE;
	const openAnalysis = isComplete ? actions.onOpenAnalysis : undefined;
	const deleteNow = isComplete ? undefined : actions.onDeleteNow;
	const close = isComplete ? undefined : actions.onClose;
	const deleteBatch =
		phase === DsaiIngestionProgressStatus.ERROR ? actions.onDeleteBatch : undefined;

	if (!openAnalysis && !deleteNow && !close && !deleteBatch) {
		return null;
	}

	return (
		<Group
			gap="xs"
			p="sm"
			wrap="nowrap"
			style={{ borderTop: '1px solid var(--mantine-color-gray-4)' }}
		>
			{deleteBatch && (
				<Group gap="xs" wrap="nowrap">
					<DeleteBatchButton
						onDelete={deleteBatch}
						isDeleting={actions.isDeletingBatch}
					/>
				</Group>
			)}
			<Group gap="xs" wrap="nowrap" ml="auto">
				{deleteNow && (
					<Button
						size="height-36"
						variant="default"
						fw={600}
						onClick={deleteNow}
						loading={actions.isDeletingNow}
						data-testid={
							TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_PROCESSING_DELETE_NOW_BUTTON
						}
					>
						{tCapManage('dealstack_ai.ingest.processing.delete_now')}
					</Button>
				)}
				{close && (
					<Button
						size="height-36"
						fw={600}
						onClick={close}
						data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_PROCESSING_CLOSE_BUTTON}
					>
						{tCapManage('dealstack_ai.ingest.processing.close')}
					</Button>
				)}
				{openAnalysis && (
					<Button
						size="height-36"
						fw={600}
						onClick={openAnalysis}
						data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_OPEN_ANALYSIS_BUTTON}
					>
						{tCapManage('dealstack_ai.ingest.processing.open_analysis')}
					</Button>
				)}
			</Group>
		</Group>
	);
};
